import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy, HOWDEN" },
      {
        name: "description",
        content:
          "How HOWDEN collects, uses, and protects your personal information.",
      },
      { property: "og:title", content: "Privacy Policy, HOWDEN" },
      {
        property: "og:description",
        content:
          "How HOWDEN collects, uses, and protects your personal information.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-20 lg:px-12 lg:py-32">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        Legal
      </p>
      <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-6xl">
        Privacy Policy
      </h1>
      <p className="mt-6 text-xs uppercase tracking-luxe text-muted-foreground">
        Last updated: May 2026
      </p>

      <div className="mt-16 space-y-12 text-base leading-relaxed text-muted-foreground">
        <section>
          <h2 className="font-serif text-2xl text-foreground">Overview</h2>
          <p className="mt-4">
            HOWDEN ("we", "us", "our") respects your privacy. This policy
            explains what information we collect when you visit howdendiamonds.com
            or purchase from us, why we collect it, and the choices you have.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            Information we collect
          </h2>
          <ul className="mt-4 list-disc space-y-2 pl-6">
            <li>
              <strong className="text-foreground">Account details:</strong> name,
              email, and password when you create an account.
            </li>
            <li>
              <strong className="text-foreground">Order details:</strong>{" "}
              shipping address, billing address, and order history.
            </li>
            <li>
              <strong className="text-foreground">Payment information:</strong>{" "}
              processed securely by our payment provider; we do not store full
              card numbers.
            </li>
            <li>
              <strong className="text-foreground">Correspondence:</strong>{" "}
              messages you send via our contact form or email.
            </li>
            <li>
              <strong className="text-foreground">Usage data:</strong> pages
              viewed, device and browser metadata, and cookies for site
              performance.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">How we use it</h2>
          <p className="mt-4">
            We use your information only to fulfil orders, provide customer
            service, send transactional and (with consent) marketing emails,
            improve the site, and meet legal obligations.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            Sharing with third parties
          </h2>
          <p className="mt-4">
            We share data only with the service providers necessary to operate
            the business, our e-commerce platform, payment processor, shipping
            carriers, email provider, and analytics. Each is bound by
            confidentiality and data-protection terms. We never sell your data.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">Your rights</h2>
          <p className="mt-4">
            You may request a copy of the data we hold about you, ask us to
            correct or delete it, withdraw marketing consent at any time, or
            export it in a portable format. Email{" "}
            <a
              href="mailto:hello@howden.diamonds"
              className="border-b border-hairline text-foreground hover:border-foreground"
            >
              hello@howden.diamonds
            </a>{" "}
            to exercise these rights.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">
            Cookies &amp; tracking
          </h2>
          <p className="mt-4">
            HOWDEN does not use third-party advertising or cross-site tracking
            cookies. We do not run Google Analytics, Meta Pixel, TikTok Pixel,
            or any similar tracker. The only data stored in your browser is
            first-party and essential to the site:
          </p>
          <ul className="mt-4 list-disc space-y-2 pl-6">
            <li>your shopping cart and selection</li>
            <li>your cookie preference, once chosen</li>
            <li>your sign-in session, if you create an account</li>
          </ul>
          <p className="mt-4">
            When you proceed to checkout, Shopify (our payment and order
            processor) sets its own cookies on its domain to complete the
            transaction. Those are governed by Shopify's privacy policy.
          </p>
          <p className="mt-4">
            We also send a strict Referrer-Policy and Permissions-Policy with
            every page, and disable Federated Learning of Cohorts (FLoC), so
            your browsing here is not used to profile you elsewhere.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-foreground">Contact</h2>
          <p className="mt-4">
            Questions about this policy? Write to{" "}
            <a
              href="mailto:hello@howden.diamonds"
              className="border-b border-hairline text-foreground hover:border-foreground"
            >
              hello@howden.diamonds
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
